import { invitationService } from '@/services/invitationService';
import {
  ExtendedCreateProjectData,
  ProjectInvitation,
  ProjectMember,
  SendInvitationData,
  TeamRole,
} from '@/types';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Mail,
  RefreshCw,
  Search,
  Send,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Input } from '../forms';
import AddTeamMemberDrawer from '../team/AddTeamMemberDrawer';
import TeamMemberCard from '../team/TeamMemberCard';

interface TeamStepProps {
  data: Partial<ExtendedCreateProjectData> & { id?: string };
  updateData: (data: Partial<ExtendedCreateProjectData>) => void;
  setValid: (isValid: boolean) => void;
}

interface InviteResult {
  success: ProjectInvitation[];
  failed: { email: string; error: string }[];
}

const TeamStep: React.FC<TeamStepProps> = ({ data, updateData, setValid }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [invitations, setInvitations] = useState<ProjectInvitation[]>([]);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<InviteResult | null>(null);
  const [showInviteResult, setShowInviteResult] = useState(false);

  // Get team members and invitations
  const teamMembers = data.detailedTeamMembers || [];

  const filteredMembers = teamMembers.filter(
    member =>
      member.user?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.user?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.user?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredInvitations = invitations.filter(
    invitation =>
      invitation.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invitation.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    setValid(true); // Team members are optional
  }, [teamMembers]); // Removed setValid from dependencies

  const handleAddMember = async (memberData: any) => {
    // During project creation (when projectId is temp or undefined),
    // always add members locally without sending invitations
    const isProjectCreation =
      !data.id || data.id === 'temp-project' || data.id?.startsWith('temp-');

    if (!isProjectCreation && memberData.sendInvitation && memberData.email) {
      // Send invitation via email only for existing projects
      await handleSendInvitations([
        {
          email: memberData.email,
          role: memberData.role,
          permissions: memberData.permissions,
          message: memberData.message,
        },
      ]);
    } else {
      // Add as direct member (store locally for project creation or existing projects)
      const newMember: ProjectMember = {
        id: `temp-${Date.now()}`,
        userId: memberData.userId || `temp-user-${Date.now()}`,
        projectId: data.id || 'temp-project',
        role: memberData.role,
        permissions: memberData.permissions.map((id: string) => ({
          id,
          name: '',
          description: '',
          category: 'project_management' as const,
        })),
        status: isProjectCreation ? 'pending' : 'active',
        assignedAt: new Date(),
        user: memberData.userId
          ? undefined
          : {
              id: `temp-user-${Date.now()}`,
              firstName: memberData.firstName || '',
              lastName: memberData.lastName || '',
              email: memberData.email,
              phone: memberData.phone,
              avatar: undefined,
              title: undefined,
              company: undefined,
              expertise: [],
              isActive: true,
            },
      };

      updateData({
        detailedTeamMembers: [...teamMembers, newMember],
      });
    }

    setIsDrawerOpen(false);
  };

  const handleSendInvitations = async (
    inviteData: Array<{
      email: string;
      role: TeamRole;
      permissions: string[];
      message?: string;
    }>
  ) => {
    setIsInviting(true);
    setInviteResult(null);

    try {
      const sendData: SendInvitationData = {
        emails: inviteData.map(invite => invite.email),
        role: inviteData[0].role, // For bulk invites, we'll use the first role
        permissions: inviteData[0].permissions,
        message: inviteData[0].message,
        projectId: data.id || 'temp-project',
      };

      const result = await invitationService.sendInvitations(sendData);

      if (result.success && result.data) {
        const newInvitations = result.data.success;
        setInvitations(prev => [...prev, ...newInvitations]);

        setInviteResult({
          success: result.data.success,
          failed: result.data.failed,
        });
        setShowInviteResult(true);

        // Auto-hide result after 5 seconds
        setTimeout(() => setShowInviteResult(false), 5000);
      } else {
        throw new Error(result.message || 'Failed to send invitations');
      }
    } catch (error: any) {
      console.error('Failed to send invitations:', error);
      setInviteResult({
        success: [],
        failed: inviteData.map(invite => ({
          email: invite.email,
          error: error.message || 'Failed to send invitation',
        })),
      });
      setShowInviteResult(true);
    } finally {
      setIsInviting(false);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      const result = await invitationService.resendInvitation(invitationId);
      if (result.success) {
        // Update invitation metadata
        setInvitations(prev =>
          prev.map(inv =>
            inv.id === invitationId
              ? {
                  ...inv,
                  metadata: {
                    userExists: inv.metadata?.userExists || false,
                    userId: inv.metadata?.userId,
                    remindersSent: (inv.metadata?.remindersSent || 0) + 1,
                    lastReminderAt: new Date(),
                  },
                }
              : inv
          )
        );
      }
    } catch (error) {
      console.error('Failed to resend invitation:', error);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const result = await invitationService.cancelInvitation(invitationId);
      if (result.success) {
        setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      }
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
    }
  };

  const handleRemoveMember = (memberId: string) => {
    updateData({
      detailedTeamMembers: teamMembers.filter(member => member.id !== memberId),
    });
  };

  const getRoleColor = (role: TeamRole, isActive: boolean = true) => {
    if (!isActive) return 'bg-gray-50 text-[#6E7077]';

    switch (role) {
      case 'project_manager':
        return 'bg-brand-50 text-brand-700';
      case 'contractor':
        return 'bg-[var(--brand-primary-light)] text-[var(--brand-primary)]';
      case 'architect':
        return 'bg-purple-50 text-purple-700';
      case 'engineer':
        return 'bg-green-50 text-green-700';
      default:
        return 'bg-gray-50 text-[#6E7077]';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-50 text-green-700';
      case 'pending':
        return 'bg-yellow-50 text-yellow-700';
      case 'accepted':
        return 'bg-green-50 text-green-700';
      case 'declined':
        return 'bg-red-50 text-red-700';
      case 'expired':
        return 'bg-gray-50 text-gray-700';
      default:
        return 'bg-gray-50 text-[#6E7077]';
    }
  };

  const getInvitationStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'accepted':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'declined':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'expired':
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const totalTeam = teamMembers.length + invitations.length;

  return (
    <div className="space-y-6 font-['Inter',sans-serif]">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        `}
      </style>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#1F1F1F] leading-[1.4em]">Team Members</h3>
          <p className="text-sm text-[#6E7077] mt-1 font-normal leading-[1.75em]">
            Add team members to your project and send them invitations to join.
          </p>
          {(!data.id || data.id === 'temp-project' || data.id?.startsWith('temp-')) && (
            <div className="mt-2 p-3 bg-brand-50 border border-brand-200 rounded-lg">
              <p className="text-xs text-brand-700">
                <strong>Note:</strong> Email invitations will be sent automatically after the
                project is created successfully.
              </p>
            </div>
          )}
        </div>
        <button
          onClick={() => setIsDrawerOpen(true)}
          disabled={isInviting}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-bold rounded-lg text-white bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--brand-primary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors uppercase tracking-wide leading-[1.2102272851126534em]"
        >
          {isInviting ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <UserPlus className="h-4 w-4 mr-2" />
              Add Member
            </>
          )}
        </button>
      </div>

      {/* Invitation Results */}
      {showInviteResult && inviteResult && (
        <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Mail className="h-5 w-5 text-brand-primary" />
            </div>
            <div className="ml-3 flex-1">
              <h4 className="text-sm font-bold text-brand-900 leading-[1.4em]">
                Invitation Results
              </h4>
              <div className="mt-2 space-y-2">
                {inviteResult.success.length > 0 && (
                  <p className="text-sm text-green-700 font-normal leading-[1.75em]">
                    ✅ {inviteResult.success.length} invitation(s) sent successfully
                  </p>
                )}
                {inviteResult.failed.length > 0 && (
                  <div className="text-sm text-red-700">
                    <p className="font-medium">
                      ❌ {inviteResult.failed.length} invitation(s) failed:
                    </p>
                    <ul className="mt-1 space-y-1">
                      {inviteResult.failed.map((failure, index) => (
                        <li key={index} className="ml-4">
                          • {failure.email}: {failure.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowInviteResult(false)}
              className="ml-3 text-brand-400 hover:text-brand-primary"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <Input
            label=""
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search team members..."
            leftElement={<Search className="h-4 w-4 text-[#6E7077]" />}
          />
        </div>
        <div className="text-sm text-[#6E7077] font-normal leading-[1.75em]">
          {totalTeam} total • {teamMembers.length} joined • {invitations.length} invited
        </div>
      </div>

      {/* Team Members List */}
      {totalTeam === 0 ? (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-[#6E7077]" />
          <h3 className="mt-4 text-lg font-bold text-[#1F1F1F] leading-[1.4em]">
            No team members yet
          </h3>
          <p className="mt-2 text-sm text-[#6E7077] font-normal leading-[1.75em]">
            Get started by adding team members to your project.
          </p>
          <button
            onClick={() => setIsDrawerOpen(true)}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-bold rounded-lg text-white bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--brand-primary)] transition-colors uppercase tracking-wide leading-[1.2102272851126534em]"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add First Member
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Active Team Members */}
          {filteredMembers.length > 0 && (
            <div>
              <h4 className="text-base font-bold text-[#1F1F1F] mb-3 leading-[1.4em]">
                Active Members ({filteredMembers.length})
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredMembers.map(member => (
                  <TeamMemberCard
                    key={member.id}
                    member={member}
                    onEdit={() => {}} // Not used during project creation
                    onRemove={() => handleRemoveMember(member.id)}
                    onStatusToggle={() => {}} // Not used during project creation
                    currentUserCanManage={true}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Pending Invitations */}
          {filteredInvitations.length > 0 && (
            <div>
              <h4 className="text-base font-bold text-[#1F1F1F] mb-3 leading-[1.4em]">
                Pending Invitations ({filteredInvitations.length})
              </h4>
              <div className="grid gap-4">
                {filteredInvitations.map(invitation => (
                  <div
                    key={invitation.id}
                    className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                          <Mail className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-base font-bold text-[#1F1F1F] leading-[1.4em]">
                              {invitation.email}
                            </h4>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide leading-[1.2102272851126534em] ${getRoleColor(invitation.role)}`}
                            >
                              {invitation.role.replace('_', ' ')}
                            </span>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide leading-[1.2102272851126534em] ${getStatusColor(invitation.status)}`}
                            >
                              {getInvitationStatusIcon(invitation.status)}
                              <span className="ml-1">{invitation.status}</span>
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className="text-sm text-[#6E7077] font-normal leading-[1.75em]">
                              Invited by {invitation.invitedByName} on{' '}
                              {formatDate(invitation.invitedAt)}
                            </span>
                            {invitation.metadata?.remindersSent &&
                              invitation.metadata.remindersSent > 0 && (
                                <span className="text-xs text-yellow-600 font-medium leading-[1.75em]">
                                  {invitation.metadata.remindersSent} reminder(s) sent
                                </span>
                              )}
                          </div>
                          <span className="text-xs text-[#6E7077] font-normal leading-[1.75em]">
                            Expires on {formatDate(invitation.expiresAt)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {invitation.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleResendInvitation(invitation.id)}
                              className="p-2 text-[#6E7077] hover:text-[var(--brand-primary)] hover:bg-[var(--brand-primary-light)] rounded-lg transition-colors"
                              title="Resend invitation"
                            >
                              <Send className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleCancelInvitation(invitation.id)}
                              className="p-2 text-[#6E7077] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Cancel invitation"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-brand-900 leading-[1.75em]">
            Team Summary: {totalTeam} total members
          </span>
          <span className="text-sm font-normal leading-[1.75em]">
            <span className="text-green-600 font-medium">{teamMembers.length} active</span>
            {invitations.length > 0 && (
              <span className="text-yellow-600 font-medium ml-3">{invitations.length} pending</span>
            )}
          </span>
        </div>
      </div>

      {/* Add Team Member Drawer */}
      <AddTeamMemberDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSubmit={handleAddMember}
        projectId={data.id || 'temp-project'}
      />
    </div>
  );
};

export default TeamStep;
